import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { 
  Brain, 
  Sparkles, 
  AlertTriangle, 
  Lightbulb, 
  TrendingUp,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock
} from 'lucide-react';

// ============================================================================
// AI INSIGHTS WIDGET - Dashboard Component
// ============================================================================

const AIInsightsWidget = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [healthScore, setHealthScore] = useState(0);
  const [metrics, setMetrics] = useState({ urgent_count: 0, opportunity_count: 0 });

  useEffect(() => {
    if (user?.id) {
      fetchQuickInsight();
    }
  }, [user?.id]);

  const fetchQuickInsight = async () => {
    try {
      const response = await axios.get(`${API}/ai-insights/quick-insight`, {
        params: { user_id: user.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setInsight(response.data.insight);
        setHealthScore(response.data.health_score);
        setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch quick insight:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch(type) {
      case 'alert': return <Clock className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'opportunity': return <Lightbulb className="w-5 h-5 text-emerald-500" />;
      case 'positive': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      default: return <Sparkles className="w-5 h-5 text-indigo-500" />;
    }
  };

  const getInsightBgColor = (type) => {
    switch(type) {
      case 'alert': return 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800';
      case 'warning': return 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800';
      case 'opportunity': return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800';
      case 'positive': return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800';
      default: return 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800';
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <div className="rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 p-4 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-100 dark:border-zinc-800 bg-gradient-to-r from-violet-600/5 to-indigo-600/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm">AI Insights</h3>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Analisa bisnis otomatis</p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-xl font-bold ${getHealthColor(healthScore)}`}>
              {healthScore}
            </div>
            <div className="text-xs text-slate-500 dark:text-zinc-400">Health Score</div>
          </div>
        </div>
      </div>
      
      {/* Main Insight */}
      <div className="p-4">
        {insight && (
          <div className={`rounded-lg border p-3 ${getInsightBgColor(insight.type)}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getInsightIcon(insight.type)}
              </div>
              <p className="text-sm text-slate-700 dark:text-zinc-300 leading-relaxed">
                {insight.message}
              </p>
            </div>
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
          {metrics.urgent_count > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-xs text-slate-600 dark:text-zinc-400">
                {metrics.urgent_count} urgent
              </span>
            </div>
          )}
          {metrics.opportunity_count > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-xs text-slate-600 dark:text-zinc-400">
                {metrics.opportunity_count} peluang
              </span>
            </div>
          )}
          <div className="flex-1"></div>
          <Link 
            to="/ai-insights"
            className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            Lihat Detail
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsWidget;
